import { useState, useEffect } from 'react'
import LicenseForm from './LicenseForm'
import { apiFetch } from '../utils/api'

const LicenseAdministrationContent = () => {
  const [licenses, setLicenses] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [editingLicense, setEditingLicense] = useState(null)
  const [isEditing, setIsEditing] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState(null)

  const loadLicenses = async () => {
    try {
      const res = await apiFetch('/api/admin/licenses')
      const data = await res.json()
      if (res.ok) {
        setLicenses(data.licenses || [])
      } else {
        setError(data.error || 'Failed to load licenses')
      }
    } catch (err) {
      setError('Network error while loading licenses')
      console.error('Error loading licenses:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadLicenses()
  }, [])

  const handleAdd = () => {
    setEditingLicense(null)
    setIsEditing(false)
    setShowForm(true)
  }

  const handleEdit = (license) => {
    setEditingLicense(license)
    setIsEditing(true)
    setShowForm(true)
  }

  const handleDelete = (license) => {
    setDeleteTarget(license)
  }

  const confirmDelete = async () => {
    try {
      const res = await apiFetch(`/api/admin/licenses/${deleteTarget.id}`, { method: 'DELETE' })
      if (res.ok) {
        setLicenses(prev => prev.filter(l => l.id !== deleteTarget.id))
      } else {
        const data = await res.json()
        setError(data.error || 'Failed to delete license')
      }
    } catch (err) {
      setError('Network error. Please check backend server.')
      console.error('Delete license error:', err)
    } finally {
      setDeleteTarget(null)
    }
  }

  const getUserDisplay = (license) => {
    // The backend always sends a user object (zero value for device licenses), so the
    // device binding is decided by deviceId, not by a missing user.
    if (license.deviceId) {
      const id = license.deviceId
      const short = id.length > 12 ? `${id.slice(0, 12)}…` : id
      return <span title={id}>{`Device: ${short}`}</span>
    }
    if (!license.user) {
      return license.userId
    }
    if (license.user.first_name && license.user.last_name) {
      return `${license.user.first_name} ${license.user.last_name}`
    }
    return license.user.email || license.userId
  }

  const handleSave = async (licenseData) => {
    setError('')
    try {
      const url = isEditing
        ? `/api/admin/licenses/${editingLicense.id}`
        : '/api/admin/licenses'
      const res = await apiFetch(url, { method: isEditing ? 'PUT' : 'POST', body: JSON.stringify(licenseData) })
      const data = await res.json()
      if (res.ok) {
        const saved = data.license
        // Reload the license with full relations (User, ProductPlan)
        const reloadRes = await apiFetch(`/api/admin/licenses/${saved.id}`)
        const reloadData = await reloadRes.json()
        if (reloadRes.ok) {
          const fullLicense = reloadData.license
          setLicenses(prev => isEditing
            ? prev.map(l => l.id === fullLicense.id ? fullLicense : l)
            : [...prev, fullLicense]
          )
        } else if (isEditing) {
          // Fallback if reload fails
          setLicenses(prev => prev.map(l => l.id === saved.id ? saved : l))
        } else {
          setLicenses(prev => [...prev, saved])
        }
        setShowForm(false)
        setEditingLicense(null)
        setIsEditing(false)
      } else {
        setError(data.error || 'Failed to save license')
      }
    } catch (err) {
      setError('Network error. Please check backend server.')
      console.error('Save license error:', err)
    }
  }

  const handleCancel = () => {
    setShowForm(false)
    setEditingLicense(null)
    setIsEditing(false)
  }

  if (loading) {
    return (
      <div className="content-section">
        <h2>Licenses</h2>
        <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
          <div className="loading-spinner" style={{ margin: '0 auto 16px' }}></div>
          Loading licenses...
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="content-section">
        <h2>Licenses</h2>
        <div className="error-message">{error}</div>
        <button className="btn-primary" onClick={loadLicenses}>Retry</button>
      </div>
    )
  }

  return (
    <div className="content-section">
      <h2>Licenses</h2>
      <div className="user-controls">
        <button className="btn-primary" onClick={handleAdd}>Add License</button>
      </div>
      <div className="users-table">
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>User</th>
              <th>Software</th>
              <th>Plan</th>
              <th>Status</th>
              <th>Start Date</th>
              <th>End Date</th>
              <th>Source</th>
              <th>Transaction ID</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {licenses.length === 0 ? (
              <tr><td colSpan="10" style={{ textAlign: 'center', color: '#9ca3af' }}>No licenses found</td></tr>
            ) : (
              licenses.map(l => (
                <tr key={l.id}>
                  <td>{l.id}</td>
                  <td>{getUserDisplay(l)}</td>
                  <td>{l.productPlan?.software_product?.name || '-'}</td>
                  <td>{l.productPlan ? l.productPlan.name : l.productId}</td>
                  <td>{l.status}</td>
                  <td>{new Date(l.startDate).toLocaleDateString()}</td>
                  <td>{l.endDate ? new Date(l.endDate).toLocaleDateString() : '-'}</td>
                  <td>{l.source}</td>
                  <td>{l.externalTransactionId}</td>
                  <td>
                    <button className="btn-edit" onClick={() => handleEdit(l)}>Edit</button>
                    <button className="btn-delete" onClick={() => handleDelete(l)}>Delete</button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {showForm && (
        <LicenseForm
          license={editingLicense}
          onSave={handleSave}
          onCancel={handleCancel}
          isEditing={isEditing}
        />
      )}
      {deleteTarget && (
        <div className="user-form-overlay">
          <div className="user-form-container">
            <div className="user-form-header">
              <h3>Delete License</h3>
              <button className="close-btn" onClick={() => setDeleteTarget(null)}>×</button>
            </div>
            <p style={{ color: '#e5e7eb', marginBottom: '16px' }}>
              Are you sure you want to delete license "{deleteTarget.id}"?
            </p>
            <div className="form-actions">
              <button type="button" className="btn-cancel" onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button type="button" className="btn-delete" onClick={confirmDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default LicenseAdministrationContent 